import React, { useCallback, useEffect } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Monitor as MonitorIcon, FolderTree, Mail, Users, ScrollText, LogOut, ExternalLink } from 'lucide-react';
import { APP_CONFIG } from '../../config/appConfig';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

const navItems = [
  { path: '/admin/monitors', label: 'Monitors', icon: MonitorIcon },
  { path: '/admin/groups', label: 'Groups', icon: FolderTree },
  { path: '/admin/recipients', label: 'Recipients', icon: Mail },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/audit', label: 'Audit Log', icon: ScrollText },
];

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = window.localStorage.getItem('auth_token');
  const username = window.localStorage.getItem('auth_user') || 'admin';

  const logout = useCallback(() => {
    window.localStorage.removeItem('auth_token');
    window.localStorage.removeItem('auth_user');
    window.localStorage.removeItem('auth_timezone');
    window.localStorage.removeItem('auth_last_activity_at');
    navigate('/admin/login');
  }, [navigate]);

  useEffect(() => {
    if (!token) return;

    const touchActivity = () => {
      window.localStorage.setItem('auth_last_activity_at', String(Date.now()));
    };

    const ensureActiveSession = () => {
      const lastRaw = window.localStorage.getItem('auth_last_activity_at');
      const last = Number(lastRaw || 0);
      if (!last || Number.isNaN(last) || (Date.now() - last) > IDLE_TIMEOUT_MS) {
        logout();
      }
    };

    ensureActiveSession();

    const activityEvents: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(evt => window.addEventListener(evt, touchActivity, { passive: true }));
    const interval = window.setInterval(ensureActiveSession, 15000);

    return () => {
      activityEvents.forEach(evt => window.removeEventListener(evt, touchActivity));
      window.clearInterval(interval);
    };
  }, [token, logout]);

  if (!token) return <Navigate to="/admin/login" replace />;

  return (
    <div className="h-screen flex bg-slate-100 overflow-hidden">
      <aside className="w-60 bg-white border-r border-slate-200 flex flex-col h-screen">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-200">
          <img src="/emerson-logo.png" alt="Emerson" className="w-11 h-11 rounded-lg object-contain" />
          <span className="font-bold text-slate-800">{APP_CONFIG.TITLE}</span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-auto">
          {navItems.map(({ path, label, icon: Icon }) => {
            const active = location.pathname === path;
            return (
              <Link
                key={path}
                to={path}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  active ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon size={18} /> {label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200 space-y-1 mt-auto bg-white">
          <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100">
            <ExternalLink size={18} /> Public Dashboard
          </Link>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 text-left"
          >
            <LogOut size={18} /> Sign out ({username})
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto h-screen">{children}</main>
    </div>
  );
};

export default AdminLayout;
